import { lazy, Suspense, useEffect, useState } from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import Header from "./components/layout/header/Header";
import ScrollToTop from "./components/layout/ScrollToTop";
import RouteSeo from "./components/layout/RouteSeo";
import GlobalCapability from "./pages/globalCapability/GlobalCapability";
import WhyUs from "./pages/whyUs/WhyUs";
import GlobalCapDetail from "./components/globalCapDetail/GlobalCapDetail";
import BlogPost from "./components/layout/blogPost/BlogPost";
import BlogMain from "./pages/blogMain/BlogMain";
import MediaMain from "./pages/mediaMain/MediaMain";

const Home = lazy(() => import("./pages/home/Home"));
const Contact = lazy(() => import("./pages/contact/Contact"));
const CareersPage = lazy(() => import("./pages/careersPage/CareersPage"));
const AboutUs = lazy(() => import("./pages/aboutUs/AboutUs"));
const AiInfraServices = lazy(() => import("./pages/aiInfraServices/AiInfraServices"));
const DataAnnotation = lazy(() => import("./components/dataAnnotation/DataAnnotation"));
const AnnotationServicePage = lazy(() =>
  import("./components/annotationServicePage/AnnotationServicePage")
);
const DataCollectionMainService = lazy(() =>
  import("./components/dataCollectionMainService/DataCollectionMainService")
);
const CollectionDetailPage = lazy(() =>
  import("./components/collectionDetailPage/CollectionDetailPage")
);
const GenerativeAI = lazy(() => import("./components/generativeAI/GenerativeAI"));
const Industries = lazy(() => import("./pages/industries/Industries"));
const IndustryDetailPage = lazy(() =>
  import("./components/industryDetailPage/IndustryDetailPage")
);
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("./pages/TermsOfUse"));

const HomeCta = lazy(() => import("./pages/home/homeCta/HomeCta"));
const MiniCta = lazy(() => import("./components/layout/miniCta/MiniCta"));
const CommonBanner = lazy(() => import("./components/layout/commonBanner/CommonBanner"));
const Footer = lazy(() => import("./components/layout/footer/Footer"));

// Old WordPress-era URLs that Google and other sites still link to.
// Each one is sent to its current page. (nginx also 301s these on the
// server, which is what search engines rely on - this is the fallback.)
const LEGACY_REDIRECTS = {
  "/service": "/ai-infrastructure",
  "/service/data-annotation": "/ai-infrastructure/ai-data-operations",
  "/blog": "/resources/blogs",
};

// /blog/<slug>  ->  /resources/blogs/<slug>
function LegacyBlogRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/resources/blogs/${slug}`} replace />;
}

// Any other old /service/<something> page goes to the services hub.
// Add an exact entry to LEGACY_REDIRECTS above if it has a closer match.
function LegacyServiceRedirect() {
  return <Navigate to="/ai-infrastructure" replace />;
}

const HIDE_HOME_CTA = ["/careers", "/contact-us", "/global-capability"];

const HIDE_HOME_CTA_PREFIXES = ["/global-capability/"];

const MINI_CTA_PATHS = ["/ai-infrastructure/ai-data-operations", "/industries"];

const MINI_CTA_PREFIXES = [
  "/ai-infrastructure/ai-data-operations/",
  "/industries/",
];

function useIdleReady() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const run = () => setReady(true);

    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(run, { timeout: 2500 });
      return () => window.cancelIdleCallback(id);
    }

    const id = window.setTimeout(run, 1200);
    return () => window.clearTimeout(id);
  }, []);

  return ready;
}

function RouteFallback() {
  return <div className="route-fallback" aria-hidden="true" />;
}

function NotFound() {
  return (
    <div className="min-h-[60vh] bg-[var(--color-page)]">
      <section className="container section">
        <div className="max-w-3xl">
          <p className="text-eyebrow mb-4 text-[var(--color-brand)]">
            Error / 404
          </p>
          <h1 className="text-heading-1 text-[var(--color-ink)]">
            Page not found
          </h1>
          <p className="mt-5 max-w-2xl text-body-lg">
            The page you are looking for does not exist or may have moved.
          </p>
          <a href="/" className="btn btn-primary mt-8">
            Back to homepage
          </a>
        </div>
      </section>
    </div>
  );
}

export default function App() {
  const { pathname } = useLocation();
  const ready = useIdleReady();
  const normalizedPath =
    pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const showHomeCta =
    !HIDE_HOME_CTA.includes(normalizedPath) &&
    !HIDE_HOME_CTA_PREFIXES.some((prefix) => normalizedPath.startsWith(prefix));
  const showMiniCta =
    MINI_CTA_PATHS.includes(normalizedPath) ||
    MINI_CTA_PREFIXES.some((prefix) => normalizedPath.startsWith(prefix));

  return (
    <>
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <ScrollToTop />
      <RouteSeo />
      <Header />

      <main id="main-content">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/contact-us" element={<Contact />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/ai-infrastructure" element={<AiInfraServices />} />
            <Route
              path="/ai-infrastructure/ai-data-operations"
              element={<DataAnnotation />}
            />
            <Route
              path="/ai-infrastructure/ai-data-operations/:slug"
              element={<AnnotationServicePage />}
            />
            <Route
              path="/ai-infrastructure/data-collection"
              element={<DataCollectionMainService />}
            />
            <Route
              path="/ai-infrastructure/data-collection/:slug"
              element={<CollectionDetailPage />}
            />
            <Route
              path="/ai-infrastructure/generative-ai"
              element={<GenerativeAI />}
            />
            <Route
              path="/global-capability"
              element={<GlobalCapability />}
            />
            <Route path="/global-capability/:slug" element={<GlobalCapDetail />} />
            <Route path="/industries" element={<Industries />} />
            <Route path="/industries/:slug" element={<IndustryDetailPage />} />
            <Route path="/why-us" element={<WhyUs />} />
            <Route path="/resources/blogs" element={<BlogMain />} />
            <Route path="/resources/media" element={<MediaMain />} />
            <Route path="/resources/blogs/:slug" element={<BlogPost />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/website-terms" element={<TermsOfUse />} />
            {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
              <Route
                key={from}
                path={from}
                element={<Navigate to={to} replace />}
              />
            ))}
            <Route path="/blog/:slug" element={<LegacyBlogRedirect />} />
            <Route path="/service/:slug" element={<LegacyServiceRedirect />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>

        {ready && (
          <Suspense fallback={null}>
            {showMiniCta && <MiniCta />}
            {showHomeCta && <HomeCta />}
          </Suspense>
        )}
      </main>

      {ready && (
        <Suspense fallback={null}>
          <CommonBanner />
          <Footer />
        </Suspense>
      )}
    </>
  );
}
